import type { Feature, FeatureCollection, MultiPolygon, Polygon, Position } from "geojson";
import { normalizePointsFC, type CoreCollection, type SamplePointCollection } from "@domain/Geo/sample";
import type { Job } from "../types";

/** The four editable geometry slices of a Job — boundary polygon, sample
 *  zones, sample points (normalised to SamplePointCollection), and cores.
 *  Any can be null when the job hasn't loaded that geometry. */
export interface JobGeoData {
	boundary: FeatureCollection | null;
	zones: FeatureCollection | null;
	points: SamplePointCollection | null;
	cores: CoreCollection | null;
}

/** Two color modes the JobMap renders in:
 *   - "boundary" — only the boundary polygon matters (other layers hidden);
 *     a job has geodata only if it has a boundary polygon geometry.
 *   - "standard" — any of boundary/points/zones counts as having geodata. */
export type JobMapColorMode = "boundary" | "standard";

/**
 * Returns the boundary polygon (or multipolygon) for a job — the first
 * Polygon/MultiPolygon feature in `job.files.boundaryGeoJSON`. Returns null
 * when the job has no boundary file or the first feature isn't a polygon.
 */
export function getJobBoundaryGeometry(job: Job): Polygon | MultiPolygon | null {
	const boundary = job.files?.boundaryGeoJSON;
	const geom = boundary?.features?.[0]?.geometry;
	if (!geom) return null;
	if (geom.type === "Polygon" || geom.type === "MultiPolygon") {
		return geom as Polygon | MultiPolygon;
	}
	return null;
}

/**
 * Combines EVERY boundary polygon feature into one Polygon/MultiPolygon geometry
 * (not just the first). `Geo.merge` concatenates boundaries as one feature per
 * source job rather than unioning them, so this collapses that back to a single
 * geometry. Returns null when the features carry no polygon. Purely structural
 * (no turf union), so overlapping rings are kept as-is.
 *
 * Used by `mergeJobs` to dissolve a merged boundary into a single feature, so
 * every downstream consumer that reads only `features[0]` still draws the whole
 * shape.
 */
export function combineBoundaryPolygons(features: Feature[] | undefined): Polygon | MultiPolygon | null {
	if (!features?.length) return null;

	const polygons: Position[][][] = [];
	for (const feature of features) {
		const geom = feature.geometry;
		if (!geom) continue;
		if (geom.type === "Polygon") polygons.push(geom.coordinates);
		else if (geom.type === "MultiPolygon") polygons.push(...geom.coordinates);
	}

	if (polygons.length === 0) return null;
	if (polygons.length === 1) return { type: "Polygon", coordinates: polygons[0] };
	return { type: "MultiPolygon", coordinates: polygons };
}

/**
 * Predicate: does the job have any geodata to render?
 *
 * In "boundary" color mode the answer hinges only on the presence of a
 * boundary polygon — the only layer that mode draws.
 * In "standard" mode any of boundary / points / zones counts.
 */
export function jobHasGeodata(job: Job, mode: JobMapColorMode): boolean {
	if (mode === "boundary") return getJobBoundaryGeometry(job) !== null;
	return (
		(job.files?.boundaryGeoJSON?.features?.length ?? 0) > 0 ||
		(job.files?.pointsGeoJSON?.features?.length ?? 0) > 0 ||
		(job.files?.zonesGeoJSON?.features?.length ?? 0) > 0
	);
}

/**
 * Predicate: do none of the given jobs carry any boundary/zones/points
 * geometry? Used by the import submission paths to decide whether files are
 * required. Implemented in terms of `jobHasGeodata` so the definition of
 * "has geometry" stays in one place.
 */
export function allJobsHaveNoFiles(jobs: Job[]): boolean {
	return !jobs.some((job) => jobHasGeodata(job, "standard"));
}

/**
 * Pulls the four editable geometry slices off a Job and normalises the
 * points layer (so the GUI gets a consistent SamplePoint shape regardless
 * of source). Returns all-null when the job has no files at all.
 *
 * Used by the JobMap edit pipeline; reusable from any future surface that
 * needs the editable subset of a job's geometry (CLI exporters, sync logic).
 */
export function jobGeoData(job: Job): JobGeoData {
	const rawPoints = job.files?.pointsGeoJSON as FeatureCollection | undefined;
	return {
		boundary: (job.files?.boundaryGeoJSON as FeatureCollection) ?? null,
		zones: (job.files?.zonesGeoJSON as FeatureCollection) ?? null,
		points: rawPoints ? normalizePointsFC(rawPoints) : null,
		cores: null,
	};
}
